import { AlertTriangle } from 'lucide-react';
import type { AlertSeverity, AlertType } from '../../types';
import { SEVERITY_INFO, ALERT_TYPE_INFO } from '../../types';

interface AlertBadgeProps {
  count?: number;
  severity?: AlertSeverity;
  type?: AlertType;
  showIcon?: boolean;
}

export function AlertBadge({ count, severity, type, showIcon = true }: AlertBadgeProps) {
  // If count is provided, show a simple count badge
  if (count !== undefined) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium whitespace-nowrap dark:bg-red-900/40 dark:text-red-300">
        {showIcon && <AlertTriangle size={12} />}
        {count}
      </span>
    );
  }

  // If severity is provided, show a severity-colored badge
  if (severity) {
    const info = SEVERITY_INFO[severity];
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${info.bgColor} ${info.color}`}
      >
        {showIcon && <AlertTriangle size={12} />}
        {type ? ALERT_TYPE_INFO[type].label : info.label}
      </span>
    );
  }

  // Default fallback
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium whitespace-nowrap dark:bg-gray-700 dark:text-gray-300">
      {showIcon && <AlertTriangle size={12} />}
      Alert
    </span>
  );
}

interface AlertDetailProps {
  type: AlertType;
  severity: AlertSeverity;
  details: Record<string, unknown>;
  onDismiss?: () => void;
  dismissed?: boolean;
}

export function AlertDetail({ type, severity, details, onDismiss, dismissed }: AlertDetailProps) {
  const severityInfo = SEVERITY_INFO[severity];
  const typeInfo = ALERT_TYPE_INFO[type];

  const formatDetail = (key: string, value: unknown): string => {
    if (key.includes('time') && typeof value === 'number') {
      return `${(value / 1000).toFixed(1)}s`;
    }
    if (key.includes('similarity') && typeof value === 'number') {
      return `${(value * 100).toFixed(1)}%`;
    }
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return String(value);
  };

  return (
    <div
      className={`rounded-lg border p-3 ${
        dismissed ? 'opacity-50 bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700' : `${severityInfo.bgColor} ${severity === 'critical' ? 'border-red-200 dark:border-red-800' : severity === 'warning' ? 'border-amber-200 dark:border-amber-800' : 'border-blue-200 dark:border-blue-800'}`
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className={dismissed ? 'text-gray-400' : severityInfo.color} />
          <div>
            <span className={`font-medium text-sm ${dismissed ? 'text-gray-500' : severityInfo.color}`}>
              {typeInfo.label}
            </span>
            <p className="text-xs text-gray-600 mt-0.5 dark:text-gray-400">{typeInfo.description}</p>
          </div>
        </div>
        {!dismissed && onDismiss && (
          <button
            onClick={onDismiss}
            className="text-xs font-medium text-gray-500 transition-colors duration-150 hover:text-gray-700 px-2 py-1 rounded hover:bg-white/50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700/50"
          >
            Dismiss
          </button>
        )}
        {dismissed && (
          <span className="text-xs text-gray-400 dark:text-gray-500">Dismissed</span>
        )}
      </div>
      {Object.keys(details).length > 0 && (
        <div className="mt-2 text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1 dark:text-gray-400">
          {Object.entries(details).map(([key, value]) => (
            <span key={key}>
              {key.replace(/_/g, ' ')}: <strong>{formatDetail(key, value)}</strong>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
